/**
 * <MultiSelect /> — drop-in replacement for native <select multiple>.
 *
 * Looks like a regular select trigger; opens a checkbox panel below.
 * Click outside to close. Match-anywhere search filter.
 */
import { useEffect, useRef, useState } from "react";
import { ChevronDown, X, Check } from "lucide-react";

export default function MultiSelect({
  options = [],            // [{ label, value }]
  value = [],              // string[]
  onChange,                // (next: string[]) => void
  placeholder = "Select…",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const valueSet = new Set(value);
  const selected = options.filter((o) => valueSet.has(o.value));
  const filtered = query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;

  const toggle = (v) => {
    const next = valueSet.has(v) ? value.filter((x) => x !== v) : [...value, v];
    onChange(next);
  };
  const remove = (v) => onChange(value.filter((x) => x !== v));
  const clear = (e) => { e.stopPropagation(); onChange([]); };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full min-h-[44px] py-2 px-3 rounded bg-input-secondary border border-gray-700 text-left flex items-center gap-1.5 flex-wrap focus:outline-none focus:border-button-primary"
      >
        {selected.length === 0 && (
          <span className="opacity-50 text-sm">{placeholder}</span>
        )}
        {selected.map((o) => (
          <span
            key={o.value}
            className="inline-flex items-center gap-1 bg-button-primary/20 border border-button-primary/40 text-text-primary text-xs px-2 py-0.5 rounded"
          >
            {o.label}
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => { e.stopPropagation(); remove(o.value); }}
              className="hover:text-button-primary cursor-pointer"
            >
              <X className="w-3 h-3" />
            </span>
          </span>
        ))}
        <span className="ml-auto flex items-center gap-1">
          {selected.length > 0 && (
            <X
              className="w-4 h-4 opacity-50 hover:opacity-100"
              onClick={clear}
            />
          )}
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-full bg-bg-primary border border-gray-700 rounded shadow-2xl overflow-hidden">
          <div className="p-2 border-b border-gray-700">
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className="w-full bg-input-secondary px-3 py-1.5 rounded text-sm border border-gray-700 focus:outline-none focus:border-button-primary"
            />
          </div>
          <ul className="max-h-56 overflow-auto">
            {filtered.length === 0 ? (
              <li className="px-3 py-2 text-sm opacity-50">No matches.</li>
            ) : (
              filtered.map((o) => {
                const checked = valueSet.has(o.value);
                return (
                  <li
                    key={o.value}
                    onClick={() => toggle(o.value)}
                    className="px-3 py-2 text-sm flex items-center gap-2 cursor-pointer hover:bg-bg-secondary"
                  >
                    <span
                      className={`w-4 h-4 rounded border flex items-center justify-center ${
                        checked
                          ? "bg-button-primary border-button-primary"
                          : "border-gray-600"
                      }`}
                    >
                      {checked && <Check className="w-3 h-3 text-white" />}
                    </span>
                    {o.label}
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
